// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import siteMessage from "@insite/client-framework/SiteMessage";
import { CountryCollectionModel } from "@insite/client-framework/Types/ApiModels";

export interface ZipCodeCookie {
    country: string;
    zipCode: string;
}

export const getZipCodeAddressCookie = (): ZipCodeCookie | null => {
    if (typeof window === "undefined") {
        return null;
    }

    const cookieString = getCookie("zip-code");

    if (!cookieString || cookieString.trim().length <= 0) {
        return null;
    }

    try {
        return JSON.parse(cookieString);
    } catch (parseError) {
        console.error(parseError);
        return null;
    }
};

export const saveZipCodeAddress = (dispatch: any, result: ZipCodeCookie) => {
    if (typeof window !== "undefined") {
        setCookie("zip-code", JSON.stringify(result));
    }

    dispatch({ type: "xHeader/update", data: { key: "zipCodeAddress", value: result } } as any);
};

export const extractGoogleAddressDetails = (
    response?: google.maps.GeocoderResult[],
    countries?: CountryCollectionModel,
) => {
    const result: { country: string; zipCode: string; error: string } = {
        country: "",
        zipCode: "",
        error: "",
    };

    if (response === undefined) {
        result.error = siteMessage("Custom_ZipCode_Failed").toString();
        return result;
    }

    if (response.length === 0) {
        result.error = siteMessage("Custom_ZipCode_Invalid").toString();
        return result;
    }

    response[0].address_components.forEach(component => {
        if (component.types.includes("country")) {
            result.country = component.short_name;
        } else if (component.types.includes("postal_code")) {
            result.zipCode = component.long_name;
        }
    });

    if (countries?.countries && !countries.countries.find(c => c.abbreviation === result.country)) {
        result.error = siteMessage("Custom_ZipCode_Outside_US").toString();
        return result;
    }

    if (!result.zipCode || !result.country) {
        result.error = siteMessage("Custom_ZipCode_Invalid").toString();
        return result;
    }

    return result;
};
